import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
// @mui
import {
  Button,
  Card,
  Typography,
  Stack,
  Box,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';

import { EntityContext } from '../../../entity';

// ----------------------------------------------------------------------

const TEMPLATE_TYPE_LABELS = {
  document: 'Documento',
  endorsement: 'Termo de Endosso',
};

function truncateKey(key, maxLen = 28) {
  if (!key) return '';
  return key.length > maxLen ? `${key.substring(0, maxLen)}...` : key;
}

// ----------------------------------------------------------------------

TemplateCard.propTypes = {
  template: PropTypes.object,
  onDeleted: PropTypes.func,
};

export default function TemplateCard({ template, onDeleted }) {
  const navigate = useNavigate();
  const { deleteTemplate, getTemplate } = useContext(EntityContext);

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [inviteKeyCopied, setInviteKeyCopied] = useState(false);

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteTemplate(template.template_key);
      setDeleteDialogOpen(false);
      if (onDeleted) onDeleted();
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDownload = (templateKey, filename) => {
    getTemplate(templateKey).then((data) => {
      downloadFile(data.url, `${filename}.docx`);
    });
  };

  const downloadFile = (filePath, fileName) => {
    fetch(filePath, { method: 'GET' })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      });
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(template.template_key).then(() => {
      setKeyCopied(true);
    });
  };

  const handleCopyInviteKey = () => {
    navigator.clipboard.writeText(template.template_key).then(() => {
      setInviteKeyCopied(true);
    });
  };

  return (
    <>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Stack spacing={2} sx={{ p: 2.5, flexGrow: 1 }}>
          {/* Header */}
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1.5,
                bgcolor: 'primary.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ArticleOutlinedIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap title={template.template_name}>
                {template.template_name || 'Sem nome'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {TEMPLATE_TYPE_LABELS[template.template_type] || template.template_type || 'Documento'}
              </Typography>
            </Box>
          </Stack>

          {/* Template key */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              bgcolor: 'background.neutral',
              borderRadius: 1,
              px: 1.5,
              py: 0.75,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', color: 'text.secondary', flexGrow: 1, userSelect: 'all' }}
              title={template.template_key}
            >
              {truncateKey(template.template_key)}
            </Typography>
            <Tooltip title="Copiar chave">
              <IconButton size="small" onClick={handleCopyKey} sx={{ flexShrink: 0 }}>
                <ContentCopyIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Divider />

        {/* Actions — 2 colunas iguais no mobile, linha compacta no desktop */}
        <Stack direction="row" flexWrap="wrap" sx={{ px: 1, py: 0.5 }}>
          <Button
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={() => handleDownload(template.template_key, template.template_name)}
            sx={{ color: 'text.secondary', width: { xs: '50%', sm: 'auto' }, justifyContent: 'flex-start' }}
          >
            Baixar
          </Button>
          <Button
            size="small"
            startIcon={<NoteAddOutlinedIcon />}
            onClick={() => navigate(`/dashboard/templates/${template.template_key}/generate-document`)}
            sx={{ color: 'text.secondary', width: { xs: '50%', sm: 'auto' }, justifyContent: 'flex-start' }}
          >
            Gerar
          </Button>
          <Button
            size="small"
            startIcon={<PeopleOutlineIcon />}
            onClick={() => setInviteModalOpen(true)}
            sx={{ color: 'primary.main', width: { xs: '50%', sm: 'auto' }, justifyContent: 'flex-start' }}
          >
            Convidar
          </Button>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setDeleteDialogOpen(true)}
            sx={{ color: 'error.main', width: { xs: '50%', sm: 'auto' }, justifyContent: 'flex-start', ml: { sm: 'auto' } }}
          >
            Excluir
          </Button>
        </Stack>
      </Card>

      {/* Modal de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
        aria-labelledby="delete-template-dialog-title"
      >
        <DialogTitle id="delete-template-dialog-title">Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o template <strong>{template.template_name}</strong>? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit" disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Excluindo...' : 'Sim, excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de convite */}
      <Dialog
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="invite-dialog-title"
      >
        <DialogTitle id="invite-dialog-title">
          Convidar para assinatura automática
          <IconButton
            aria-label="fechar"
            onClick={() => setInviteModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Compartilhe o código abaixo com o signatário que você deseja autorizar a assinar
              automaticamente documentos gerados por este template.
            </Typography>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Template
              </Typography>
              <Typography variant="subtitle2">{template.template_name}</Typography>
            </Box>

            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                bgcolor: 'background.neutral',
                borderRadius: 1,
                px: 2,
                py: 1.25,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', flexGrow: 1, wordBreak: 'break-all', userSelect: 'all' }}
              >
                {template.template_key}
              </Typography>
              <Tooltip title={inviteKeyCopied ? 'Copiado!' : 'Copiar código'}>
                <IconButton size="small" onClick={handleCopyInviteKey} color={inviteKeyCopied ? 'success' : 'default'}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                Como o signatário usa este código:
              </Typography>
              <Stack spacing={0.5}>
                {[
                  'Acessa a página "Chancela Automática" na plataforma',
                  'Cria uma nova credencial',
                  'Clica em "Usar código de convite" e informa este código',
                ].map((step, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                    <Typography
                      variant="caption"
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        mt: 0.1,
                      }}
                    >
                      {i + 1}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            <Alert severity="info" variant="outlined" sx={{ '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
              O signatário não precisa fazer parte da sua organização na plataforma, mas precisa ter
              um plano de chancela automática ativo.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteModalOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar: chave copiada (card) */}
      <Snackbar
        open={keyCopied}
        autoHideDuration={2000}
        onClose={() => setKeyCopied(false)}
        message="Chave copiada!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Snackbar: chave copiada (modal) */}
      <Snackbar
        open={inviteKeyCopied}
        autoHideDuration={2000}
        onClose={() => setInviteKeyCopied(false)}
        message="Código copiado!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
